import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import './Sales.css';
import {
  ApiError,
  BoothEvent,
  DailyTotal,
  PeriodTotals,
  SaleReceipt,
  SalesFilter,
  SalesFocus,
  SalesStats,
  deleteSale,
  downloadSalesCsv,
  fetchRecentSales,
  fetchSalesStats,
} from '../../api/client';
import { EventContext } from '../../api/eventContext';
import { formatDollars } from '../../shared/functions/formatter';
import { Button, Card, CardTitle, EmptyState, Field, Input, Menu, MenuItem, Meta, Notice, SectionHeader, Select, Stack } from '../../ui';
import DeleteSaleDialog from './DeleteSaleDialog';

type SalesProps = {
  onUnauthorized: () => void;
  /** Scope to open on when arriving from the events page. */
  focus: SalesFocus | null;
  /** Switch to the events page; the report is unusable with no events at all. */
  onOpenEvents: () => void;
};

/** Which slice of sales the page is showing. Empty means every sale. */
type Scope = { eventId?: number; unassigned?: boolean };

/** The reporting windows, as digits so the row stays compact; titles spell them out. */
const PERIODS = [
  { label: '7D', days: 7, name: 'Last 7 days' },
  { label: '14D', days: 14, name: 'Last 14 days' },
  { label: '30D', days: 30, name: 'Last 30 days' },
  { label: '90D', days: 90, name: 'Last 90 days' },
] as const;

const PAGE_SIZE = 20;

/** "2026-09-16" -> "16 Sep". Parsed as local since it is a calendar date. */
function shortDate(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/**
 * Totals for the window immediately before the selected one.
 *
 * Derived from a doubled request by taking its earlier half: the API only offers
 * windows ending today, so there is no way to ask for the previous period
 * directly. Revenue, sales and the average they imply are all exact. Items sold
 * is not in the daily series, which is why no items comparison is shown.
 */
function previousWindow(longer: SalesStats): PeriodTotals | null {
  const half = Math.floor(longer.daily.length / 2);
  if (half <= 0) return null;
  const earlier = longer.daily.slice(0, half);
  const revenue = earlier.reduce((sum, entry) => sum + entry.revenue, 0);
  const transactions = earlier.reduce((sum, entry) => sum + entry.transactions, 0);
  return {
    revenue,
    transactions,
    items_sold: earlier.reduce((sum, entry) => sum + entry.items_sold, 0),
    average_sale: transactions > 0 ? revenue / transactions : 0,
  };
}

/** "2026-09-16" -> "16 Sep 2026". */
function fullDate(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * The till reports in Singapore time, while the API stores UTC.
 *
 * SQLite hands the backend naive datetimes and they reach the client without an
 * offset, so the value must be read as UTC before it is shown in SGT — otherwise
 * the browser would render the UTC wall clock as if it were local.
 */
const DISPLAY_TIME_ZONE = 'Asia/Singapore';

function parseUtc(value: string): Date | null {
  const iso = /(?:Z|[+-]\d{2}:?\d{2})$/.test(value) ? value : `${value}Z`;
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function timeOf(isoTimestamp: string): string {
  const parsed = parseUtc(isoTimestamp);
  return parsed
    ? parsed.toLocaleTimeString('en-SG', {
        timeZone: DISPLAY_TIME_ZONE,
        hour: '2-digit',
        minute: '2-digit',
      })
    : isoTimestamp;
}

function dayOf(isoTimestamp: string): string {
  const parsed = parseUtc(isoTimestamp);
  return parsed
    ? parsed.toLocaleDateString('en-SG', {
        timeZone: DISPLAY_TIME_ZONE,
        day: 'numeric',
        month: 'short',
      })
    : '';
}

function formatRange(event: BoothEvent): string {
  const start = fullDate(event.start_date);
  if (!event.end_date || event.end_date === event.start_date) {
    return start;
  }
  return start + ' – ' + fullDate(event.end_date);
}

/** How a metric moved against the equivalent period before it. */
function change(current: number, previous: number): { text: string; direction: 'up' | 'down' | 'flat' } | null {
  if (previous <= 0) return null;
  const ratio = (current - previous) / previous;
  const percent = Math.round(Math.abs(ratio) * 100);
  if (percent === 0) return { text: 'no change', direction: 'flat' };
  return {
    text: (ratio > 0 ? 'up ' : 'down ') + percent + '%',
    direction: ratio > 0 ? 'up' : 'down',
  };
}

const KpiCard: FC<{
  label: string;
  value: string;
  note?: string;
  /** The headline figure for the report. */
  primary?: boolean;
  delta?: ReturnType<typeof change>;
}> = ({ label, value, note, primary = false, delta }) => (
  <div className={primary ? 'salesCard salesCardPrimary' : 'salesCard'}>
    <span className="salesCardLabel">{label}</span>
    <span className="salesCardValue money">{value}</span>
    {delta ? (
      <span className={'salesKpiDelta salesKpiDelta--' + delta.direction}>
        {/* Direction is spelled out; the arrow is decoration, not the message. */}
        <span aria-hidden="true">{delta.direction === 'up' ? '↑' : delta.direction === 'down' ? '↓' : '→'}</span> {delta.text}
      </span>
    ) : (
      <span className="salesCardNote">{note}</span>
    )}
  </div>
);

/**
 * The four numbers that answer "how did I do?" for the selected period.
 *
 * All four describe the same window, so they can be read together. Today is not
 * one of them - it is a different time dimension - so it sits beneath as a note
 * rather than competing for the same row.
 */
const SummaryCards: FC<{
  period: PeriodTotals;
  today: PeriodTotals;
  previous: PeriodTotals | null;
  comparisonLabel: string;
  periodNote: string;
}> = ({ period, today, previous, comparisonLabel, periodNote }) => (
  <>
    <div className="salesCards">
      <KpiCard
        label="Revenue"
        value={formatDollars(period.revenue)}
        note={periodNote}
        primary
        delta={previous ? change(period.revenue, previous.revenue) : undefined}
      />
      <KpiCard
        label="Sales"
        value={String(period.transactions)}
        note={periodNote}
        delta={previous ? change(period.transactions, previous.transactions) : undefined}
      />
      <KpiCard
        label="Average sale"
        value={formatDollars(period.average_sale)}
        note={periodNote}
        delta={previous ? change(period.average_sale, previous.average_sale) : undefined}
      />
      {/* Items sold compares only when the earlier window actually sold units:
          the daily series now carries items_sold, and change() declines a zero
          base rather than inventing a percentage. */}
      <KpiCard
        label="Items sold"
        value={String(period.items_sold)}
        note={periodNote}
        delta={previous ? change(period.items_sold, previous.items_sold) : undefined}
      />
    </div>
    <p className="salesToday">
      <span className="salesCardToday">
        <span className="salesCardLabel">Today</span> <span className="salesCardValue money">{formatDollars(today.revenue)}</span> ·{' '}
        {today.transactions} sale
        {today.transactions === 1 ? '' : 's'}
      </span>
      {previous && <span className="salesTodayNote">compared with {comparisonLabel}</span>}
    </p>
  </>
);

/**
 * Revenue per day, with a readable scale and exact figures on demand.
 *
 * The figures appear in a line under the chart rather than a floating tooltip:
 * it cannot be clipped or cover the data, it is reachable by keyboard, and a tap
 * on a phone focuses a bar the same way a hover does on a desktop.
 */
const RevenueChart: FC<{ daily: DailyTotal[] }> = ({ daily }) => {
  const [active, setActive] = useState<number | null>(null);
  const peak = Math.max(...daily.map((entry) => entry.revenue), 0);
  const total = daily.reduce((sum, entry) => sum + entry.revenue, 0);

  if (daily.length === 0 || total <= 0) {
    return (
      <Card className="salesPanel">
        <CardTitle>Revenue over time</CardTitle>
        <EmptyState title="No sales in this period" description="Try another date range or event." />
      </Card>
    );
  }

  // Thin the labels out rather than shrinking them into an unreadable row.
  const everyNth = Math.ceil(daily.length / 8);
  const shown = active === null ? null : daily[active];

  return (
    <Card className="salesPanel">
      <div className="salesPanelHead">
        <CardTitle>Revenue over time</CardTitle>
        <Meta className="salesPanelNote">
          {daily.length} days · {formatDollars(total)}
        </Meta>
      </div>
      <div className="salesChartWrap">
        <div className="salesChartAxis" aria-hidden="true">
          <span>{formatDollars(peak)}</span>
          <span>{formatDollars(peak / 2)}</span>
          <span>{formatDollars(0)}</span>
        </div>
        <div className="salesChart">
          {daily.map((entry, index) => (
            <button
              type="button"
              key={entry.date}
              className={'salesBar' + (entry.revenue > 0 ? ' salesBarFilled' : '') + (active === index ? ' salesBarActive' : '')}
              aria-label={
                fullDate(entry.date) +
                ': ' +
                formatDollars(entry.revenue) +
                ' from ' +
                entry.transactions +
                (entry.transactions === 1 ? ' sale' : ' sales')
              }
              onMouseEnter={() => setActive(index)}
              onMouseLeave={() => setActive(null)}
              onFocus={() => setActive(index)}
              onBlur={() => setActive(null)}
            >
              <span className="salesBarFill" style={{ height: Math.max(2, Math.round((entry.revenue / peak) * 100)) + '%' }} />
              <span className="salesBarLabel">{index % everyNth === 0 ? shortDate(entry.date) : ''}</span>
            </button>
          ))}
        </div>
      </div>
      <p className="salesChartReadout" role="status">
        {shown ? (
          <>
            <strong>{fullDate(shown.date)}</strong>
            <span className="money">{formatDollars(shown.revenue)}</span>
            <span>
              {shown.transactions} sale{shown.transactions === 1 ? '' : 's'}
            </span>
          </>
        ) : (
          <Meta as="span">Hover or focus a bar for its figures.</Meta>
        )}
      </p>
    </Card>
  );
};

const PaymentMix: FC<{ payments: SalesStats['payments'] }> = ({ payments }) => (
  <Card className="salesPanel salesPanelMix">
    <CardTitle>Payment mix</CardTitle>
    {payments.length === 0 ? (
      <Meta>Nothing recorded yet.</Meta>
    ) : (
      <ul className="salesMix">
        {payments.map((entry) => (
          <li key={entry.payment}>
            <div className="salesMixHead">
              <span className={'salesPayTag salesPayTag--' + entry.payment}>{entry.payment}</span>
              <span className="money">{formatDollars(entry.revenue)}</span>
            </div>
            <div className="salesMixTrack">
              <span className="salesMixFill" style={{ width: Math.round(entry.share * 100) + '%' }} />
            </div>
            <span className="salesMixNote">
              {Math.round(entry.share * 100)}% · {entry.transactions} sale{entry.transactions === 1 ? '' : 's'}
            </span>
          </li>
        ))}
      </ul>
    )}
  </Card>
);

/**
 * What sold, ranked by a dimension the operator chooses.
 *
 * Both rankings come from the server, each aggregated over every item in the
 * period: re-sorting one truncated list in the browser would silently misrank.
 */
const TopSellers: FC<{ stats: SalesStats }> = ({ stats }) => {
  const [rank, setRank] = useState<'revenue' | 'quantity'>('revenue');
  const items = rank === 'revenue' ? stats.items_by_revenue : stats.top_items;
  const periodRevenue = stats.period.revenue;

  return (
    <Card className="salesPanel salesPanelTop">
      <div className="salesPanelHead">
        <CardTitle>Top sellers</CardTitle>
        <div className="salesRank" role="group" aria-label="Rank top sellers by">
          {(['revenue', 'quantity'] as const).map((option) => (
            <Button key={option} size="sm" aria-pressed={rank === option} onClick={() => setRank(option)}>
              {option === 'revenue' ? 'Revenue' : 'Quantity'}
            </Button>
          ))}
        </div>
      </div>
      {items.length === 0 ? (
        <Meta>Nothing sold in this period.</Meta>
      ) : (
        <table className="salesTable salesTableCompact">
          <thead>
            <tr>
              <th align="left">Item</th>
              <th align="right">Qty</th>
              <th align="right">Revenue</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.title}>
                <td align="left">
                  <span className="salesItemTitle" title={item.title}>
                    {item.title}
                  </span>
                  {periodRevenue > 0 && (
                    <span className="salesItemShare">{Math.round((item.revenue / periodRevenue) * 1000) / 10}% of revenue</span>
                  )}
                </td>
                {/* Both values stay visible; the one matching the active rank
                    toggle takes the stronger text role and the other is muted. */}
                <td align="right" className={rank === 'quantity' ? 'salesValue salesValueActive' : 'salesValue'}>
                  {item.quantity}
                </td>
                <td align="right" className={'money salesValue' + (rank === 'revenue' ? ' salesValueActive' : '')}>
                  {formatDollars(item.revenue)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
};

/**
 * How the bundle offers performed.
 *
 * Uses only what each sale recorded at the time: the discount it gave, and the
 * label naming the offers it used. Nothing here is recomputed from today's bundle
 * definitions, so editing a bundle never rewrites this history. Offers are
 * grouped by the label exactly as stored - a bundle's name can itself contain an
 * "x 2", so splitting the label into offer names would misattribute sales.
 */
const BundlePerformance: FC<{ stats: SalesStats }> = ({ stats }) => (
  <Card className="salesPanel salesPanelBundle">
    <CardTitle>Bundle performance</CardTitle>
    {stats.bundle_transactions === 0 ? (
      <Meta>No bundle offers were used in this period.</Meta>
    ) : (
      <>
        <div className="salesBundleStats">
          <div>
            <span className="salesBundleValue">{stats.bundle_transactions}</span>
            <span className="salesBundleLabel">sale{stats.bundle_transactions === 1 ? '' : 's'} with an offer</span>
          </div>
          <div>
            <span className="salesBundleValue money">{formatDollars(stats.bundle_revenue)}</span>
            <span className="salesBundleLabel">taken on those sales</span>
          </div>
          <div>
            <span className="salesBundleValue money">{formatDollars(stats.discounts)}</span>
            <span className="salesBundleLabel">customer savings</span>
          </div>
        </div>
        {stats.bundle_offers.length > 0 && (
          <div className="salesBundleOffers">
            <h3>Offers used</h3>
            <ul>
              {stats.bundle_offers.map((offer) => (
                <li key={offer.label}>
                  <span className="salesBundleOfferLabel" title={offer.label}>
                    {offer.label}
                  </span>
                  <span className="salesBundleOfferCount">
                    {offer.sales} sale{offer.sales === 1 ? '' : 's'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </>
    )}
  </Card>
);

const Sales: FC<SalesProps> = ({ onUnauthorized, focus, onOpenEvents }) => {
  const { events } = useContext(EventContext);

  // Opening from an event card asks for that booth's report. This used to work
  // because the page remounted on every view switch; now that Sales and Events are
  // two sections of one page it stays mounted, so a new request has to be noticed.
  // Adjusting during render is React's own pattern for state that follows a prop,
  // and it leaves the select's own changes alone until the next request.
  const [scope, setScope] = useState<Scope>(() => focus ?? {});
  const [handledFocus, setHandledFocus] = useState(focus);
  if (focus !== handledFocus) {
    setHandledFocus(focus);
    if (focus) setScope(focus);
  }
  const [days, setDays] = useState<number>(14);
  const [stats, setStats] = useState<SalesStats | null>(null);
  /** The equivalent window immediately before this one, when one is derivable. */
  const [previous, setPrevious] = useState<PeriodTotals | null>(null);
  const [recent, setRecent] = useState<SaleReceipt[]>([]);
  const [offset, setOffset] = useState(0);
  /** Transactions filter: typed text, and the text actually applied. */
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [payment, setPayment] = useState('all');
  /** The transaction whose row menu is open, if any. */
  const [menuFor, setMenuFor] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  /** The sale awaiting confirmation, or null when no prompt is open. */
  const [pendingDelete, setPendingDelete] = useState<SaleReceipt | null>(null);
  const [deleting, setDeleting] = useState(false);
  /** Bumped after a delete so the list and the aggregates are refetched. */
  const [reload, setReload] = useState(0);

  const selectedEvent = scope.eventId === undefined ? null : (events.find((e) => e.id === scope.eventId) ?? null);

  /**
   * The API filter for the current view.
   *
   * An event is scoped by attribution (its own span, no day window); everything
   * else is a rolling window. Memoised so the fetch effect does not re-run on
   * every render.
   */
  const filter: SalesFilter = useMemo(() => {
    if (scope.eventId !== undefined) return { eventId: scope.eventId };
    if (scope.unassigned) return { unassigned: true, days };
    return { days };
  }, [scope, days]);

  /**
   * The table's own filters, on top of the report's scope.
   *
   * Sent to the server rather than applied to the loaded page: the history is
   * paged, so filtering in the browser would search only the rows on screen.
   * The KPIs and chart above stay on the report scope, which is why these live
   * in the transactions panel rather than beside the event and period controls.
   */
  const listFilter = useMemo(
    () => ({
      ...filter,
      payment: payment === 'all' ? undefined : payment,
      q: search.trim() || undefined,
    }),
    [filter, payment, search]
  );

  /** Every method worth offering a filter for: those in the period, and on screen. */
  const paymentMethods = useMemo(() => {
    const seen = new Set<string>();
    for (const entry of stats ? stats.payments : []) seen.add(entry.payment);
    for (const sale of recent) seen.add(sale.payment);
    return Array.from(seen).sort();
  }, [stats, recent]);

  useEffect(() => {
    let cancelled = false;

    // Rows and aggregates are separate endpoints; load them together so the
    // page never shows a summary that disagrees with the table below it.
    Promise.all([
      fetchSalesStats(filter),
      fetchRecentSales(PAGE_SIZE, offset, listFilter),
      // A doubled window, so the period before this one can be measured exactly
      // rather than guessed. An event is its own span, so it has no equivalent.
      scope.eventId === undefined && days > 0 ? fetchSalesStats({ ...filter, days: days * 2 }) : Promise.resolve(null),
    ])
      .then(([nextStats, nextSales, longer]) => {
        if (cancelled) return;
        setStats(nextStats);
        setRecent(nextSales);
        setPrevious(longer ? previousWindow(longer) : null);
        setError('');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // An expired session is the app's problem, not this page's.
        if (err instanceof ApiError && err.status === 401) {
          onUnauthorized();
          return;
        }
        setError(err instanceof Error ? err.message : 'Could not load recorded sales.');
      });

    return () => {
      cancelled = true;
    };
  }, [filter, listFilter, scope.eventId, days, offset, onUnauthorized, reload]);

  const changePeriod = (next: number) => {
    setDays(next);
    // A different window means a different result set; start from the top.
    setOffset(0);
  };

  const changeScope = (value: string) => {
    setOffset(0);
    if (value === 'all') {
      setScope({});
    } else if (value === 'unassigned') {
      setScope({ unassigned: true });
    } else {
      setScope({ eventId: Number(value) });
    }
  };

  /**
   * Perform a confirmed deletion.
   *
   * The reload after success matters: the aggregates on this page include the
   * sale that just went, so leaving them stale would contradict the table.
   */
  const confirmDelete = useCallback(
    (reason: string) => {
      if (!pendingDelete) return;
      setDeleting(true);
      deleteSale(pendingDelete.id, reason)
        .then(() => {
          setPendingDelete(null);
          setError('');
          setReload((count) => count + 1);
        })
        .catch((err: unknown) => {
          if (err instanceof ApiError && err.status === 401) {
            onUnauthorized();
            return;
          }
          setError(err instanceof Error ? err.message : 'Could not delete the transaction.');
          setPendingDelete(null);
        })
        .finally(() => setDeleting(false));
    },
    [pendingDelete, onUnauthorized]
  );

  const scopeValue = scope.eventId !== undefined ? String(scope.eventId) : scope.unassigned ? 'unassigned' : 'all';
  const subtitle = selectedEvent
    ? formatRange(selectedEvent) + (selectedEvent.venue ? ' · ' + selectedEvent.venue : '')
    : scope.unassigned
      ? 'Sales recorded before a booth event was chosen.'
      : 'Every sale the cashier has recorded.';
  // With no events at all there is nothing to attribute sales to, so point the
  // operator at the events page rather than letting the till file everything as
  // Unassigned without explanation.
  const noEvents = events.length === 0;

  // Derived rather than tracked: nothing has arrived yet. Toggling a loading
  // flag inside the effect would only cause a cascading render.
  if (!stats) {
    return (
      <div className="sales">
        <SectionHeader title="Sales" description={subtitle} />
        {error ? (
          <Notice variant="error" onDismiss={() => setError('')}>
            {error}
          </Notice>
        ) : (
          <Meta>loading recorded sales…</Meta>
        )}
      </div>
    );
  }

  return (
    <div className="sales">
      <SectionHeader title="Sales" description={subtitle} />

      {/* Scope and period: these decide what every figure below describes. The
          transaction table's own search and payment filters live with it. */}
      <Card className="salesFilterBar">
        <Field label="Event" className="salesScope">
          <Select value={scopeValue} onChange={(changed) => changeScope(changed.target.value)}>
            <option value="all">All events</option>
            <option value="unassigned">Unassigned</option>
            {events.map((event) => (
              <option key={event.id} value={event.id}>
                {event.name}
              </option>
            ))}
          </Select>
        </Field>
        {/* A period is meaningless for one event: its span is the period. */}
        {selectedEvent === null && (
          <div className="salesPeriods" role="group" aria-label="Reporting period">
            {PERIODS.map((period) => (
              <Button
                key={period.days}
                size="sm"
                aria-pressed={period.days === days}
                aria-label={period.name}
                title={period.name}
                onClick={() => changePeriod(period.days)}
              >
                {period.label}
              </Button>
            ))}
          </div>
        )}
        <Button
          variant="secondary"
          className="salesExport"
          disabled={exporting}
          title="Download this view as CSV"
          onClick={() => {
            setExporting(true);
            setError('');
            downloadSalesCsv(filter)
              .catch((err: unknown) => {
                if (err instanceof ApiError && err.status === 401) {
                  onUnauthorized();
                  return;
                }
                setError(err instanceof Error ? err.message : 'Could not export the sales CSV.');
              })
              .finally(() => setExporting(false));
          }}
        >
          {exporting ? 'Exporting…' : 'Export CSV'}
        </Button>
      </Card>

      {noEvents && (
        <Notice variant="warning">
          No booth events yet. Create one so each sale is tracked under the booth it was rung up at.{' '}
          <Button size="sm" onClick={onOpenEvents}>
            create an event
          </Button>
        </Notice>
      )}

      {error && (
        <Notice variant="error" onDismiss={() => setError('')}>
          {error}
        </Notice>
      )}

      <SummaryCards
        period={stats.period}
        today={stats.today}
        previous={previous}
        comparisonLabel={'the previous ' + days + ' days'}
        // Name the window. Items sold never gets a previous-window delta, so
        // this note is the one the operator reads on that card every time.
        periodNote={
          selectedEvent
            ? 'across this event'
            : 'across the ' + (PERIODS.find((entry) => entry.days === days)?.name ?? days + ' days').toLowerCase()
        }
      />

      <RevenueChart daily={stats.daily} />

      <div className="salesGrid">
        <PaymentMix payments={stats.payments} />
        <TopSellers stats={stats} />
        <BundlePerformance stats={stats} />
      </div>

      <Card className="salesPanel">
        <div className="salesPanelHead">
          <CardTitle>Transactions</CardTitle>
          <Meta className="salesPanelNote">
            {recent.length === 0 ? 'nothing to show' : 'showing ' + (offset + 1) + '–' + (offset + recent.length)}
          </Meta>
        </div>

        {/* These filter the table only: the KPIs and chart above describe the
            event and period, which is what the top bar controls. */}
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setOffset(0);
            setSearch(searchInput);
          }}
        >
          <Stack direction="row" gap={2} className="salesTableTools">
            <Input
              type="search"
              className="salesSearchInput"
              aria-label="Search transactions"
              value={searchInput}
              placeholder="Reference, item or event"
              onChange={(changed) => setSearchInput(changed.target.value)}
            />
            <Button type="submit" size="sm">
              Search
            </Button>
            {(search !== '' || searchInput !== '' || payment !== 'all') && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchInput('');
                  setSearch('');
                  setPayment('all');
                  setOffset(0);
                }}
              >
                Clear
              </Button>
            )}
            <Select
              className="salesPaymentFilter"
              aria-label="Payment method"
              value={payment}
              onChange={(changed) => {
                setOffset(0);
                setPayment(changed.target.value);
              }}
            >
              <option value="all">All payments</option>
              {paymentMethods.map((method) => (
                <option key={method} value={method}>
                  {method}
                </option>
              ))}
            </Select>
          </Stack>
        </form>

        {recent.length === 0 ? (
          <EmptyState
            title={
              search || payment !== 'all'
                ? 'No transactions match these filters.'
                : offset > 0
                  ? 'No more transactions.'
                  : 'No sales recorded yet.'
            }
          />
        ) : (
          <table className="salesTable">
            <thead>
              <tr>
                <th align="left">Ref</th>
                <th align="left">When</th>
                {selectedEvent === null && <th align="left">Event</th>}
                <th align="left">Items</th>
                <th align="left">Payment</th>
                <th align="right">Total</th>
                <th align="right">
                  <span className="ui-visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {recent.map((sale) => (
                <tr key={sale.id}>
                  <td align="left" className="salesRef" data-label="Ref">
                    {sale.order_ref}
                  </td>
                  <td align="left" data-label="When">
                    {/* Keep the date and time together so the column takes the
                        width it needs instead of wrapping before "pm". */}
                    <span className="salesWhen">
                      {dayOf(sale.created_at)} {timeOf(sale.created_at)}
                    </span>
                  </td>
                  {selectedEvent === null && (
                    <td align="left" className="salesEventCell" data-label="Event">
                      {sale.event_name ?? 'Unassigned'}
                    </td>
                  )}
                  <td align="left" className="salesLines" data-label="Items">
                    <span className="salesItemCount">
                      {sale.items.length} item{sale.items.length === 1 ? '' : 's'}
                    </span>
                    <span className="salesItemPreview">
                      {sale.items
                        .slice(0, 2)
                        .map((line) => line.quantity + '× ' + line.title)
                        .join(', ')}
                      {sale.items.length > 2 ? ' +' + (sale.items.length - 2) : ''}
                    </span>
                  </td>
                  <td align="left" data-label="Payment">
                    <span className={'salesPayTag salesPayTag--' + sale.payment}>{sale.payment}</span>
                  </td>
                  <td align="right" className="money" data-label="Total">
                    {formatDollars(sale.total)}
                  </td>
                  <td align="right" className="salesActions">
                    <Menu
                      label={'Actions for transaction ' + sale.order_ref}
                      open={menuFor === sale.id}
                      onOpenChange={(open) => setMenuFor(open ? sale.id : null)}
                    >
                      <MenuItem
                        danger
                        onClick={() => {
                          setMenuFor(null);
                          setPendingDelete(sale);
                        }}
                      >
                        Delete transaction
                      </MenuItem>
                    </Menu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="salesPager">
          <Button size="sm" disabled={offset === 0} onClick={() => setOffset((current) => Math.max(0, current - PAGE_SIZE))}>
            ← newer
          </Button>
          <Meta as="span">{recent.length === 0 ? 'nothing to show' : 'showing ' + (offset + 1) + '–' + (offset + recent.length)}</Meta>
          <Button size="sm" disabled={recent.length < PAGE_SIZE} onClick={() => setOffset((current) => current + PAGE_SIZE)}>
            older →
          </Button>
        </div>
      </Card>

      {pendingDelete && (
        <DeleteSaleDialog
          orderRef={pendingDelete.order_ref}
          total={pendingDelete.total}
          when={dayOf(pendingDelete.created_at) + ' ' + timeOf(pendingDelete.created_at)}
          itemSummary={pendingDelete.items.map((line) => line.quantity + '× ' + line.title).join(', ')}
          busy={deleting}
          onCancel={() => setPendingDelete(null)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
};

export default Sales;
